// Car-body release (Stage 6 slice 4): the release form, the Bonifica card line, the vehicle-list
// chip and the parts-screen banner rule. Pure: no Vue, no API calls, so node:test can load it. The
// API checks everything again (api/core/compliance/waste/carBody.js); these checks only keep the
// button disabled and pick the texts.
import { romeDay } from "./sendOutForm.mjs";

export const WEIGHT_MAX = 60000; // kg, as the API
export const REASON_MAX = 500;

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

// dd/mm/yyyy of an instant, in Europe/Rome.
export function formatRomeDate(instant) {
  const day = romeDay(instant);
  if (!day) return "";
  const [year, month, date] = day.split("-");
  return `${date}/${month}/${year}`;
}

// A new form: the only area preselected, both ticks off.
export function emptyReleaseForm(view) {
  const areas = (view && view.areas) || [];
  return {
    containerId: areas.length === 1 ? areas[0].id : null,
    weightKg: "",
    confirmPartsRemoved: false,
    confirmNoHazards: false,
  };
}

// The optional weight: null when empty, a number, or NaN when it is not a valid weight (above 0,
// up to WEIGHT_MAX, one decimal at most; a comma counts as the decimal point).
export function parseWeight(value) {
  if (isBlank(value)) return null;
  const number = Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(number) || number <= 0 || number > WEIGHT_MAX) {
    return Number.NaN;
  }
  if (Math.abs(number * 10 - Math.round(number * 10)) > 1e-9) {
    return Number.NaN;
  }
  return Math.round(number * 10) / 10;
}

// Field -> i18n key of what is still missing or wrong; empty when the form can be sent.
export function releaseErrors(form) {
  const errors = {};
  if (!form || isBlank(form.containerId)) {
    errors.containerId = "acceptance.car_body.errors.area_required";
  }
  if (form && Number.isNaN(parseWeight(form.weightKg))) {
    errors.weightKg = "acceptance.car_body.errors.weight_invalid";
  }
  if (!form || form.confirmPartsRemoved !== true) {
    errors.confirmPartsRemoved = "acceptance.car_body.errors.tick_required";
  }
  if (!form || form.confirmNoHazards !== true) {
    errors.confirmNoHazards = "acceptance.car_body.errors.tick_required";
  }
  return errors;
}

// The button stays disabled until both ticks are set and the form is valid.
export function canSubmitRelease(form) {
  return Object.keys(releaseErrors(form)).length === 0;
}

// The POST body: { containerId, weightKg?, confirmPartsRemoved: true, confirmNoHazards: true }.
export function buildRelease(form) {
  const weightKg = parseWeight(form.weightKg);
  return {
    containerId: form.containerId,
    ...(weightKg === null ? {} : { weightKg }),
    confirmPartsRemoved: form.confirmPartsRemoved === true,
    confirmNoHazards: form.confirmNoHazards === true,
  };
}

export function reasonError(reason) {
  if (isBlank(reason)) return "acceptance.car_body.errors.reason_required";
  if (String(reason).trim().length > REASON_MAX) {
    return "acceptance.car_body.errors.reason_too_long";
  }
  return null;
}

// The i18n key of an API error of the release or the cancel, or null (the caller shows errorMsg).
const ERROR_KEYS = {
  depollution_not_approved: "acceptance.car_body.errors.not_approved",
  already_released: "acceptance.car_body.errors.already_released",
  confirmation_required: "acceptance.car_body.errors.tick_required",
  container_not_allowed: "acceptance.car_body.errors.area_not_allowed",
  already_sent_out: "acceptance.car_body.errors.already_sent_out",
  not_released: "acceptance.car_body.errors.not_released",
  reason_required: "acceptance.car_body.errors.reason_required",
  not_manager: "acceptance.car_body.errors.not_manager",
};

export function releaseErrorKey(err) {
  const data = err && err.response && err.response.data;
  return (data && ERROR_KEYS[data.code]) || null;
}

// The Bonifica card line "Carcassa rilasciata il … · area · da … · N ricambi" (Q4-4: the count
// saved at release). Returns the i18n params, or null when not released.
export function releaseLine(view) {
  const release = view && view.release;
  if (!release) return null;
  return {
    date: formatRomeDate(release.releasedAt),
    area: (release.container && release.container.name) || "",
    name: (release.releasedBy && release.releasedBy.name) || "",
    count:
      release.partsCountAtRelease == null ? 0 : release.partsCountAtRelease,
  };
}

// What the card offers: "Rilascia carcassa" (Manager, approved, not released; the view's
// canRelease) and "Annulla rilascio" (Manager, while the area is not sent out). sentOut: the
// area was sent out after the release, so it can no longer be cancelled (the card says why).
export function releaseActions(view) {
  return {
    canRelease: Boolean(view && view.canRelease),
    canCancel: Boolean(view && view.release && view.release.canCancel),
    sentOut: Boolean(view && view.release && view.release.sentOut),
    noArea: Boolean(
      view && (view.blocking || []).includes("no_area") && !view.release,
    ),
  };
}

// The grey "Carcassa rilasciata" chip of the vehicle lists, from one by-cars row, or null.
export function releasedChip(row) {
  return row && row.carBodyRelease && row.carBodyRelease.releasedAt
    ? { key: "acceptance.car_body.released_chip", color: "grey" }
    : null;
}

// The parts-screen banner (display only): shown only with waste on and a released car body.
// Returns { date } for the i18n text, or null.
export function bannerOf({ wasteEnabled, row }) {
  if (wasteEnabled !== true) return null;
  if (!row || !row.carBodyRelease || !row.carBodyRelease.releasedAt) {
    return null;
  }
  return { date: formatRomeDate(row.carBodyRelease.releasedAt) };
}
